<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useMetricsSnapshotStore } from '@/store/metricsSnapshotStore'
import StatusCard from './StatusCard.vue'
import AppMenuBarWithDateRange from '@/components/Menu/AppMenuBarWithDateRange.vue'
import { RefreshCw } from 'lucide-vue-next'
import type { MetricsSnapshot } from '@/models/MetricsSnapshot'
import { type LifecycleState, LIFECYCLE_STATE_OPTIONS, filterByLifecycleState } from '@/models/MetricsSnapshot'

const props = withDefaults(defineProps<{
  tenantId: string
  tenantName: string
  userId: string
  userName: string
  lifecycleState?: LifecycleState
}>(), {
  lifecycleState: 'all',
})

const emit = defineEmits<{
  'navigate-to-summary': []
  'navigate-to-tenant': [payload: { tenantId: string; tenantName: string }]
  'navigate-to-drilldown': [payload: { status?: string }]
  'update:lifecycleState': [state: LifecycleState]
}>()

const metricsStore = useMetricsSnapshotStore()

const selectedLifecycleState = ref<LifecycleState>(props.lifecycleState)

// Sync with prop changes from parent
watch(() => props.lifecycleState, (val) => {
  selectedLifecycleState.value = val
})

function handleLifecycleStateChange(value: LifecycleState) {
  selectedLifecycleState.value = value
  emit('update:lifecycleState', value)
}

// --- Data fetching ---

async function fetchData() {
  // First try to use already-loaded tenant data (which includes per-user snapshots)
  // If not available, fetch tenant metrics which includes all user data
  console.log('[UserView] fetchData called', {
    tenantId: props.tenantId,
    userId: props.userId,
    hasTenantData: !!metricsStore.perTenantData[props.tenantId],
  })
  if (!metricsStore.perTenantData[props.tenantId]) {
    console.log('[UserView] No tenant data in store, fetching...')
    await metricsStore.fetchTenantMetrics(props.tenantId, { tenantOnly: false })
  }
  // Log what we found
  const tenantSnapshots = metricsStore.perTenantData[props.tenantId]?.snapshots ?? []
  const userSnapshots = tenantSnapshots.filter((s) => s.ownerId === props.userId)
  console.log('[UserView] After fetch:', {
    totalTenantSnapshots: tenantSnapshots.length,
    userSnapshotsFound: userSnapshots.length,
    userId: props.userId,
    allOwnerIds: [...new Set(tenantSnapshots.map(s => s.ownerId))],
    sampleSnapshot: userSnapshots[0] ?? null,
  })
}

// --- Computed ---

const loading = computed(() => metricsStore.loading.perTenant[props.tenantId] ?? false)

const snapshots = computed<MetricsSnapshot[]>(() => {
  // Use tenant data filtered to this specific user
  const tenantSnapshots = metricsStore.perTenantData[props.tenantId]?.snapshots ?? []
  const userSnapshots = tenantSnapshots.filter((s) => s.ownerId === props.userId)
  // Only use "all-time" snapshots (date range > 30 days) — filter out stale daily snapshots
  const allTimeSnapshots = userSnapshots.filter((s) => {
    if (!s.dateRangeStart || !s.dateRangeEnd) return true
    const start = new Date(s.dateRangeStart).getTime()
    const end = new Date(s.dateRangeEnd).getTime()
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24)
    return daysDiff > 30
  })
  const filtered = filterByLifecycleState(allTimeSnapshots, selectedLifecycleState.value)
  console.log('[UserView] snapshots computed:', {
    tenantId: props.tenantId,
    userId: props.userId,
    selectedLifecycleState: selectedLifecycleState.value,
    tenantSnapshotsCount: tenantSnapshots.length,
    userSnapshotsCount: userSnapshots.length,
    allTimeCount: allTimeSnapshots.length,
    filteredCount: filtered.length,
  })
  return filtered
})

const aggregatedCounts = computed(() => {
  const result = metricsStore.aggregateCounts(snapshots.value)
  console.log('[UserView] aggregatedCounts:', result, 'from snapshots:', snapshots.value.length)
  return result
})

/** Find the most recent aggregatedUtc across user snapshots */
const lastAggregatedUtc = computed<string | null>(() => {
  let latest: string | null = null
  for (const snap of snapshots.value) {
    if (snap.aggregatedUtc && (!latest || snap.aggregatedUtc > latest)) {
      latest = snap.aggregatedUtc
    }
  }
  return latest
})

/** Check if snapshot data is stale (older than 24 hours) */
const isSnapshotStale = computed(() => {
  if (!lastAggregatedUtc.value) return false
  const lastDate = new Date(lastAggregatedUtc.value)
  const now = new Date()
  const hoursDiff = (now.getTime() - lastDate.getTime()) / (1000 * 60 * 60)
  return hoursDiff > 24
})

/** Human-readable "last updated" string */
const lastUpdatedLabel = computed(() => {
  if (!lastAggregatedUtc.value) return null
  const d = new Date(lastAggregatedUtc.value)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

const statusCards = computed(() => [
  { label: 'Total', count: aggregatedCounts.value.totalCount, variant: 'info' as const },
  { label: 'Succeeded', count: aggregatedCounts.value.succeededCount, variant: 'success' as const },
  { label: 'Failed', count: aggregatedCounts.value.failedCount, variant: 'danger' as const },
  { label: 'Running', count: aggregatedCounts.value.runningCount, variant: 'info' as const },
  { label: 'Pending', count: aggregatedCounts.value.pendingCount, variant: 'warning' as const },
  { label: 'Cancelled', count: aggregatedCounts.value.cancelledCount, variant: 'neutral' as const },
  { label: 'Throttled', count: aggregatedCounts.value.throttledCount, variant: 'warning' as const },
  { label: 'Rejected', count: aggregatedCounts.value.rejectedCount, variant: 'danger' as const },
])

/** Format a date range period from ISO strings */
function formatDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return '—'
  const fmt = (iso: string | null) => {
    if (!iso) return '?'
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  }
  return `${fmt(start)} – ${fmt(end)}`
}

// --- Table columns for snapshot list ---

const columns = [
  {
    title: 'Date Range',
    key: 'dateRange',
    customRender: ({ record }: { record: MetricsSnapshot }) =>
      formatDateRange(record.dateRangeStart, record.dateRangeEnd),
  },
  {
    title: 'Succeeded',
    dataIndex: 'succeededCount',
    key: 'succeededCount',
    sorter: (a: MetricsSnapshot, b: MetricsSnapshot) => a.succeededCount - b.succeededCount,
  },
  {
    title: 'Failed',
    dataIndex: 'failedCount',
    key: 'failedCount',
    sorter: (a: MetricsSnapshot, b: MetricsSnapshot) => a.failedCount - b.failedCount,
  },
  {
    title: 'Total',
    dataIndex: 'totalCount',
    key: 'totalCount',
    sorter: (a: MetricsSnapshot, b: MetricsSnapshot) => a.totalCount - b.totalCount,
  },
]

// --- Navigation ---

function handleNavigateToSummary() {
  emit('navigate-to-summary')
}

function handleNavigateToTenant() {
  emit('navigate-to-tenant', {
    tenantId: props.tenantId,
    tenantName: props.tenantName,
  })
}

function handleStatusCardClick(label: string) {
  const status = label === 'Total' ? undefined : label.toLowerCase()
  emit('navigate-to-drilldown', { status })
}

// --- Menu bar buttons ---

const navButtons = ref([
  {
    icon: RefreshCw,
    onClick: fetchData,
    animate: loading,
    title: '',
    toolTip: 'Refresh',
  },
])

// --- Lifecycle ---

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div>
    <!-- Menu bar with date range -->
    <AppMenuBarWithDateRange
      :showSearch="false"
      :showDateRange="false"
      :buttons="navButtons"
    />

    <!-- Breadcrumb navigation -->
    <nav class="mb-4 flex items-center gap-1 text-sm text-muted-foreground">
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToSummary"
      >
        Summary
      </button>
      <span class="mx-1">&gt;</span>
      <button
        class="hover:text-foreground hover:underline transition-colors"
        @click="handleNavigateToTenant"
      >
        {{ tenantName }}
      </button>
      <span class="mx-1">&gt;</span>
      <span class="text-foreground font-medium">{{ userName }}</span>
    </nav>

    <!-- Lifecycle state filter -->
    <div class="mb-4">
      <a-segmented
        v-model:value="selectedLifecycleState"
        :options="LIFECYCLE_STATE_OPTIONS"
        @change="handleLifecycleStateChange"
      />
    </div>

    <!-- Status Cards -->
    <div class="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
      <StatusCard
        v-for="card in statusCards"
        :key="card.label"
        :label="card.label"
        :count="card.count"
        :variant="card.variant"
        :loading="loading"
        @click="handleStatusCardClick(card.label)"
      />
    </div>

    <!-- Snapshot freshness indicator -->
    <div v-if="lastUpdatedLabel && !loading" class="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
      <span>Snapshot generated:</span>
      <span :class="isSnapshotStale ? 'text-yellow-500 font-medium' : ''">
        {{ lastUpdatedLabel }}
      </span>
      <span v-if="isSnapshotStale" class="inline-flex items-center gap-1 rounded-full bg-yellow-500/10 px-2 py-0.5 text-yellow-500 border border-yellow-500/30">
        ⚠ Stale — last update was more than 24 hours ago
      </span>
    </div>

    <!-- Empty state -->
    <div v-if="!loading && snapshots.length === 0" class="py-12 text-center text-muted-foreground">
      No metrics available for the selected period.
    </div>

    <!-- Snapshot list table -->
    <div v-else class="overflow-x-auto">
      <!-- Skeleton loading for table -->
      <div v-if="loading && snapshots.length === 0" class="space-y-3">
        <Skeleton v-for="i in 5" :key="i" class="h-12 w-full" />
      </div>

      <a-table
        v-else
        :dataSource="snapshots"
        :columns="columns"
        :pagination="{ showSizeChanger: snapshots.length >= 20 }"
        :rowKey="(record: MetricsSnapshot) => record.id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'dateRange'">
            {{ formatDateRange(record.dateRangeStart, record.dateRangeEnd) }}
          </template>

          <template v-else-if="column.key === 'succeededCount'">
            <Badge class="bg-green-500 hover:bg-green-500 text-white">
              {{ record.succeededCount.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'failedCount'">
            <Badge class="bg-red-500 hover:bg-red-500 text-white">
              {{ record.failedCount.toLocaleString() }}
            </Badge>
          </template>

          <template v-else-if="column.key === 'totalCount'">
            {{ record.totalCount.toLocaleString() }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>
